import { useEffect, useRef, useState } from 'react';
import { ActionIcon, Alert, Button, Group, Modal, NumberInput, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import type { Product, InstallmentEntry } from './OpportunityPageTypes';
import type { Options } from '../utility/types';
import { buildInstallmentEntries, installmentsAmountsValid, recalculateInstallmentsBelow } from '../utility/installmentAmounts';
import { monthlyInstallmentDates, nextMondayIfWeekend } from '../utility/installmentDates';
import { allocateInstallments, rowTotal, rowValid, toProduct, type ProductRow } from '../utility/productRows';

type Props = {
  opened: boolean;
  onClose: () => void;
  onSave: (products: Product[]) => void;
  onDelete: (product: Product) => void;
  products: Product[];
  productOptions: Options[];
  existingProducts: Product[];
  initialProducts?: Product[];
  opportunityClientOptions: Options[];
};
const asRow = (product: Product): ProductRow => ({ ...product, price: Number(product.price), quantity: Number(product.quantity) || 1, discountType: product.discountType || 'percent', discountValue: product.discountValue ?? (Number(product.discount) || 0) });
const validDate = (date: Date | null | undefined) => date instanceof Date && !Number.isNaN(date.getTime());

export function OpportunityProductsModal({ opened, onClose, onSave, onDelete, products, productOptions, existingProducts, initialProducts, opportunityClientOptions }: Props) {
  const { t } = useTranslation();
  const label = (key: string) => t(`opportunity.modal.${key}`);
  const initialProduct = initialProducts?.[0];
  const [packageName, setPackageName] = useState('');
  const [saveError, setSaveError] = useState(false);
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [method, setMethod] = useState<'avista' | 'parcelado'>('avista');
  const [date, setDate] = useState<Date | null>(null);
  const [client, setClient] = useState('');
  const [count, setCount] = useState(2);
  const [schedule, setSchedule] = useState<InstallmentEntry[]>([]);
  const wasOpen = useRef(false);
  const dateAnchor = useRef<Date | null>(null);
  const defaultClient = opportunityClientOptions[0]?.value || '';
  const showClients = opportunityClientOptions.length > 1;
  const currency = rows[0]?.currency || 'USD';
  const total = rows.reduce((sum, row) => sum + Math.round(rowTotal(row) * 100), 0) / 100;
  const money = (value: number) => value.toLocaleString(undefined, { style: 'currency', currency });

  useEffect(() => {
    if (opened && !wasOpen.current) {
      setSaveError(false);
      setRows(initialProducts?.map(asRow) || []);
      setPackageName(initialProduct?.salesPackageName || `${label('salesPackage')} ${new Set(existingProducts.map(item => item.salesPackageId || item.productId)).size + 1}`);
      setSelected(null);
      setMethod(initialProduct?.payment?.tipo || 'avista');
      const now = new Date();
      const billingDate = initialProduct?.payment?.data || new Date(now.getFullYear(), now.getMonth(), now.getDate());
      setDate(billingDate);
      setClient(initialProduct?.payment?.client_id || defaultClient);
      const entries = initialProduct?.payment?.parcelas || [];
      dateAnchor.current = entries[0]?.data || billingDate;
      setCount(entries.length || 2);
      setSchedule(entries.map((entry, index) => ({ ...entry, amount: (initialProducts || []).reduce((sum, product) => sum + Math.round((product.payment?.parcelas?.[index]?.amount || 0) * 100), 0) / 100 })));
    }
    wasOpen.current = opened;
  }, [opened, initialProduct, defaultClient]);

  useEffect(() => {
    if (!opened || method !== 'parcelado') return;
    setSchedule(previous => {
      const entries = installmentsAmountsValid(previous, total) && previous.length === count
        ? previous : buildInstallmentEntries(count, total, previous, defaultClient);
      const dates = monthlyInstallmentDates(dateAnchor.current || previous[0]?.data || null, count);
      return entries.map((entry, index) => ({ ...entry, data: entry.data || dates[index] }));
    });
  }, [opened, total, method, count, defaultClient]);

  const changePaymentDate = (value: Date | null) => {
    setDate(value);
    dateAnchor.current = value;
    const dates = monthlyInstallmentDates(value, count);
    setSchedule(previous => previous.map((entry,index) => ({ ...entry, data: dates[index] })));
  };

  const changeInstallmentDate = (index: number, value: Date | null) => {
    if (index === 0) {
      dateAnchor.current = value;
      const dates = monthlyInstallmentDates(value, count);
      setSchedule(previous => previous.map((entry, i) => ({ ...entry, data: dates[i] })));
    } else {
      setSchedule(previous => previous.map((entry, i) => i === index
        ? { ...entry, data: value ? nextMondayIfWeekend(value) : null } : entry));
    }
  };

  const options = productOptions.filter(option => {
    const product = products.find(item => item.productId === option.value);
    return product && !rows.some(row => row.productId === option.value) &&
      (!rows.length || product.currency === currency);
  });
  const add = () => {
    const product = products.find(item => item.productId === selected);
    if (!product || !options.some(option => option.value === selected)) return;
    setRows(previous => [...previous, asRow({ ...product, quantity: 1, discount: 0, sold: false })]);
    setSelected(null);
  };
  const change = (index: number, patch: Partial<ProductRow>) => setRows(previous => previous.map((row, i) => i === index ? { ...row, ...patch } : row));
  const paymentValid = method === 'avista'
    ? validDate(date) && (!showClients || !!client)
    : schedule.length === count && schedule.every(entry => validDate(entry.data) && (!showClients || !!entry.client_id)) && installmentsAmountsValid(schedule, total);
  const canSave = packageName.trim().length > 0 && rows.length > 0 && rows.every(rowValid) && paymentValid;
  const save = () => {
    if (!canSave) return;
    setSaveError(false);
    try {
    const allocation = method === 'parcelado' ? allocateInstallments(rows.map(rowTotal), schedule) : [];
    const packageId = initialProduct?.salesPackageId || crypto.randomUUID();
    onSave(rows.map((row, index) => ({ ...toProduct(row, method === 'avista'
      ? { tipo: 'avista', data: date, client_id: client || undefined }
      : { tipo: 'parcelado', parcelas: allocation[index] }), salesPackageId: packageId, salesPackageName: packageName.trim() })));
    onClose();
    } catch {
      setSaveError(true);
    }
  };

  return <Modal opened={opened} onClose={onClose} title={label(initialProduct ? 'editSalesPackage' : 'addSalesPackage')} centered size="min(1400px, 96vw)">
    <Stack spacing="md">
      <TextInput label={label('salesPackageName')} value={packageName} onChange={event => setPackageName(event.currentTarget.value)} maxLength={200} required />
      <Group align="flex-end" noWrap>
        <Select label={label('productSearch')} placeholder={label('selectProduct')} data={options} value={selected} onChange={setSelected} searchable clearable style={{ flex: 1 }} withinPortal
          onKeyDown={event => { if (event.key === 'Enter' && selected) { event.preventDefault(); add(); } }} />
        <Button leftIcon={<IconPlus size={16} />} onClick={add} disabled={!selected}>{label('addRow')}</Button>
      </Group>
      {!initialProduct && rows.length > 0 && <Text size="xs" color="dimmed">{label('batchCurrencyNote')}</Text>}
      <div className="opportunity-package-table-scroll">
        <Table withBorder highlightOnHover style={{ minWidth: 940 }}>
          <thead><tr>
            <th>{label('product')}</th><th>{label('quantity')}</th><th>{label('unitPrice')}</th>
            <th>{label('rowDiscount')}</th><th>{label('discountType')}</th><th>{label('total')}</th><th />
          </tr></thead>
          <tbody>{rows.map((row, index) => <tr key={row.productId}>
            <td style={{ minWidth: 230 }}><Text size="sm" weight={500}>{row.productName}</Text><Text size="xs" color="dimmed">{row.currency}</Text></td>
            <td><NumberInput aria-label={`${label('quantity')} ${row.productName}`} value={row.quantity} onChange={value => change(index, { quantity: Number(value) || 0 })} min={1} precision={0} style={{ width: 90 }} /></td>
            <td><NumberInput aria-label={`${label('unitPrice')} ${row.productName}`} value={row.price} onChange={value => change(index, { price: Number(value) || 0 })} min={0} precision={2} style={{ width: 135 }} /></td>
            <td><NumberInput aria-label={`${label('rowDiscount')} ${row.productName}`} value={row.discountValue} onChange={value => change(index, { discountValue: Number(value) || 0 })} min={0} max={row.discountType === 'percent' ? 100 : row.price * row.quantity} precision={2} error={!rowValid(row)} style={{ width: 115 }} /></td>
            <td><Select aria-label={`${label('discountType')} ${row.productName}`} value={row.discountType} onChange={value => change(index, { discountType: value as 'percent' | 'fixed', discountValue: 0 })} data={[{ value: 'percent', label: label('percentDiscount') }, { value: 'fixed', label: label('fixedDiscount') }]} style={{ width: 165 }} withinPortal /></td>
            <td style={{ whiteSpace: 'nowrap' }}>{money(rowTotal(row))}</td>
            <td>{<ActionIcon color="red" aria-label={`${label('removeRow')} ${row.productName}`} onClick={() => setRows(previous => previous.filter((_, i) => i !== index))}><IconTrash size={18} /></ActionIcon>}</td>
          </tr>)}</tbody>
        </Table>
      </div>
      {!rows.length && <Text align="center" color="dimmed" py="md">{label('emptyProducts')}</Text>}
      <Text align="right" size="lg" weight={600} aria-live="polite">{label('total')}: {money(total)}</Text>
      <Select style={{ width: '100%', maxWidth: 320 }} label={label('paymentMethod')} value={method} onChange={value => setMethod(value as 'avista' | 'parcelado')} data={[{ value: 'avista', label: label('paymentSingle') }, { value: 'parcelado', label: label('paymentInstallment') }]} required withinPortal />
      {method === 'avista' ? <Group grow align="flex-end">
        <DateInput label={label('billingDate')} value={date} onChange={changePaymentDate} required popoverProps={{ withinPortal: true }} />
        {showClients && <Select label={label('responsibleClient')} data={opportunityClientOptions} value={client} onChange={value => setClient(value || '')} required withinPortal />}
      </Group> : <>
        <NumberInput label={label('numberOfInstallments')} value={count} min={2} max={12} precision={0} onChange={value => setCount(Math.max(2, Math.min(12, Number(value) || 2)))} required />
        {schedule.map((entry, index) => <Group key={index} grow align="flex-end">
          <DateInput label={t('opportunity.modal.dateOfInstallment', { n: index + 1 })} value={entry.data} onChange={value => changeInstallmentDate(index, value)} required popoverProps={{ withinPortal: true }} />
          <NumberInput label={t('opportunity.modal.installmentAmount', { n: index + 1 })} value={entry.amount ?? 0} min={0} precision={2} onChange={value => setSchedule(previous => recalculateInstallmentsBelow(previous.map((item, i) => i === index ? { ...item, amount: Number(value) || 0 } : item), index, total))} required />
          {showClients && <Select label={t('opportunity.modal.clientForInstallment', { n: index + 1 })} value={entry.client_id || defaultClient} data={opportunityClientOptions} onChange={value => setSchedule(previous => previous.map((item, i) => i === index ? { ...item, client_id: value || '' } : item))} required withinPortal />}
        </Group>)}
        {!installmentsAmountsValid(schedule, total) && <Alert color="orange">{label('installmentTotalMismatch')}</Alert>}
      </>}
      {saveError && <Alert color="red" role="alert">{label('savePackageFailed')}</Alert>}
      <Group position="right" mt="md">
        {initialProduct && <Button color="red" variant="light" onClick={() => { onDelete(initialProduct); onClose(); }}>{t('common.delete')}</Button>}
        <Button variant="default" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={save} disabled={!canSave}>{label('saveSalesPackage')}</Button>
      </Group>
    </Stack>
  </Modal>;
}
